import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Button, Tooltip } from 'ui';
import { InfoCircleIcon } from 'ui/icons';
import { CountryCodeEnum, EntityType, UpdateEntityRequest } from 'api';

const mapEntityTypeToTexts: Record<EntityType, { name: string; description: string }> = {
  [EntityType.Individual]: {
    name: 'Individual',
    description: 'Investing as a person, not through a business or trust.',
  },
  [EntityType.Company]: {
    name: 'Company',
    description: 'Investing through a registered business entity.',
  },
  [EntityType.Trust]: {
    name: 'Trust',
    description: 'Investing on behalf of beneficiaries using a trust structure.',
  },
  [EntityType.Partnership]: {
    name: 'Partnership',
    description: 'Investing jointly with one or more business partners.',
  },
  [EntityType.Association]: {
    name: 'Association & Not for profits',
    description: 'Investing as a non-profit or community organisation.',
  },
  [EntityType.Smsf]: {
    name: 'Self Managed Superannuation Fund',
    description: 'Investing with a personal retirement fund you manage.',
  },
};

const entityOptions = Object.values(EntityType).map(type => ({
  type,
  name: mapEntityTypeToTexts[type].name,
  description: mapEntityTypeToTexts[type].description,
}));

export const SelectEntityTypeTab = ({ moveForward }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder, isLoading: isLoadingGet } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const { mutateAsync: updateInvestor, isLoading: isLoadingUpdate } = useUpdateInvestorEditMutation(
    {
      onSuccess: dataHolder => {
        if (dataHolder?.data?.new_data?.entity_data?.type) {
          //has not type
          //go to select type
          moveForward && moveForward();
          return;
        }
      },
    },
  );

  const [entityType, setEntityType] = useState(EntityType.Individual);

  const handleUpdateInvestor = useCallback(async () => {
    await updateInvestor({
      user_data: dataHolder?.data?.new_data?.user_data,
      entity_data: {
        ...dataHolder?.data?.new_data?.entity_data, // ToDo: After changed type need delete all prev fields
        type: entityType,
      } as UpdateEntityRequest,
      dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
      id: +entity_id,
    });
  }, [
    dataHolder?.data?.new_data?.entity_data,
    dataHolder?.data?.new_data?.user_data,
    dataHolder?.data?.new_data?.dividend_reinvestment,
    entityType,
    entity_id,
    updateInvestor,
  ]);

  useEffect(() => {
    if (dataHolder?.data?.new_data?.entity_data?.type) {
      moveForward && moveForward();
    }
  }, [dataHolder?.data?.new_data?.entity_data?.type, moveForward]);

  return (
    <OnboardingHolderWrapper
      title="What entity type are you investing with?"
      onClickContinue={handleUpdateInvestor}
      disableNext={isLoadingGet || isLoadingUpdate}
      isLoading={isLoadingGet || isLoadingUpdate}
    >
      <div className="flex w-full sm:h-full">
        <div className="w-full flex flex-col justify-center items-end">
          <div className="w-full flex flex-col align-middle justify-center space-y-6">
            {entityOptions.map(({ type, name, description }) => (
              <Button
                onClick={() => setEntityType(type)}
                key={'EntityType_' + type}
                variant="secondary"
                className={classNames('flex justify-between relative', {
                  'border-[#486a7b]': entityType === type,
                })}
                textAlign="left"
                disabled={
                  dataHolder?.data?.new_data?.entity_data?.country !== CountryCodeEnum.AUS &&
                  type === EntityType.Smsf
                }
              >
                <span>{name}</span>
                <Tooltip
                  relativeBlock={false}
                  position="center"
                  positionHorizontal="left"
                  variant="gray"
                  classNameTooltip="border"
                  button={<img alt="info" src={InfoCircleIcon} />}
                >
                  <span>
                    {description}
                    <br />
                    {dataHolder?.data?.new_data?.entity_data?.country !== CountryCodeEnum.AUS &&
                    type === EntityType.Smsf
                      ? 'Available in Australia only'
                      : ''}
                  </span>
                </Tooltip>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </OnboardingHolderWrapper>
  );
};
